import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../services/firebase/config';
import StripeActivationDialog from '../../components/StripeActivationDialog';

const RegisterScreen: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showStripeDialog, setShowStripeDialog] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // 1. Créer le compte utilisateur Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // 2. Créer automatiquement un compte Stripe Connect Express
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      let stripeData = null;
      let stripeError = null;

      try {
        const stripeResponse = await fetch(`${apiUrl}/api/stripe/create-connected-account`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: user.uid,
            userEmail: user.email
          })
        });

        stripeData = await stripeResponse.json();

        if (!stripeData.success) {
          stripeError = stripeData.error || 'Erreur lors de la création du compte Stripe';
          console.warn('Erreur Stripe (non bloquante):', stripeError);
        }
      } catch (stripeErr: any) {
        stripeError = stripeErr.message || 'Impossible de créer le compte Stripe';
        console.warn('Erreur Stripe (non bloquante):', stripeError);
      }

      // 3. Créer le document utilisateur dans Firestore (même si Stripe échoue)
      const userData: any = {
        email: user.email,
        createdAt: new Date(),
        profile: {
          displayName: '',
          photoURL: '',
          location: '',
          rating: 0
        },
        stripe: {
          chargesEnabled: false,
          payoutsEnabled: false,
          detailsSubmitted: false,
          onboardingCompleted: false
        }
      };

      // Si Stripe a réussi, ajouter le connectedAccountId
      if (stripeData?.success && stripeData.accountId) {
        userData.stripe.connectedAccountId = stripeData.accountId;
      }

      await setDoc(doc(db, 'users', user.uid), userData);

      // 4. Afficher le dialog d'activation Stripe (avec option skip)
      setLoading(false);
      setShowStripeDialog(true);
    } catch (err: any) {
      console.error('Erreur inscription:', err);
      setError(err.message || 'Une erreur est survenue lors de l\'inscription');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5" sx={{ mb: 3, color: '#C9B6AB' }}>
            Inscription
          </Typography>
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Adresse email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Mot de passe"
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirmer le mot de passe"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                backgroundColor: '#C9B6AB',
                '&:hover': {
                  backgroundColor: '#C1A08E',
                },
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Création du compte...
                </>
              ) : (
                "S'inscrire"
              )}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/login')}
                sx={{ color: '#C9B6AB' }}
              >
                Déjà un compte ? Connectez-vous
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Dialog d'activation Stripe après inscription */}
      <StripeActivationDialog
        open={showStripeDialog}
        onClose={() => {
          setShowStripeDialog(false);
          navigate('/dashboard', { 
            state: { 
              message: 'Compte créé avec succès ! Vous pourrez activer les paiements Stripe depuis les paramètres.' 
            } 
          });
        }}
        onActivate={() => {
          // La redirection sera gérée dans le dialog
        }}
        required={false}
      />
    </Container>
  );
};

export default RegisterScreen; 